import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  searchProductGroupSelectOptions,
  mergeProductGroupOptions,
  productGroupOptionDisplayName,
} from "../utils/productGroupOptions";
import { lineCellErrorStyle } from "../utils/lineItemFieldErrors";

export const parseNumericValue = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

export const createDefaultOldPurchaseRow = (id = 1) => ({
  id,
  product_group_id: "",
  product_group_name: "",
  pcs: "",
  ct: "",
  gwt: "",
  lwt: "",
  nwt: "",
  touch: "",
  fine_wt: "",
  total_amt: "",
  isTotalAmtManual: false,
});

const OldPurchaseTable = ({
  tableRows = [],
  setTableRows,
  productGroupOptionsCache = [],
  setProductGroupOptionsCache,
  masterGoldRate = 0,
  masterSilverRate = 0,
  showLiveRate = false,
  isSilverMode = false,
  onPoolHeaderChange = null,
  portalZIndex = 10060,
  readOnly = false,
  cardClass = "",
  buttonTableStyle = "",
  tableClass = "",
  buttonClass = "",
  defaultProductGroupLabel = "",
  fieldErrors = {},
}) => {
  const [productGroupSearchText, setProductGroupSearchText] = useState({});
  const [productGroupDropdownActive, setProductGroupDropdownActive] = useState({});
  const [productGroupRowResults, setProductGroupRowResults] = useState({});
  const [productGroupHighlightedIndex, setProductGroupHighlightedIndex] = useState({});
  const [productGroupSearchLoading, setProductGroupSearchLoading] = useState({});
  const [productGroupApiCalled, setProductGroupApiCalled] = useState({});

  const productGroupInputRefs = useRef({});
  const productGroupDropdownRefs = useRef({});
  const productGroupItemsRefs = useRef({});
  const productGroupSearchTimerRef = useRef({});
  const lastSearchedProductGroupPrefixRef = useRef({});

  // Fill the product-group input from the row when the parent sets a default
  // (Old Gold / Old Silver) or loads an existing line. Do not overwrite text
  // the user has already typed.
  useEffect(() => {
    setProductGroupSearchText((prev) => {
      const updated = { ...prev };
      let changed = false;
      tableRows.forEach((r) => {
        const name = String(r.product_group_name || "").trim();
        if (!name) return;
        if (updated[r.id] === undefined || updated[r.id] === "") {
          updated[r.id] = name;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [tableRows]);

  const getRowMetalFlags = useCallback(
    (row) => {
      const pg = (productGroupOptionsCache || []).find(
        (o) => String(o.value) === String(row.product_group_id),
      );
      const metal = String(
        pg?.metal || pg?.name || pg?.label || row.product_group_name || "",
      ).toUpperCase();
      return {
        isGold: metal.includes("GOLD") || (!isSilverMode && !metal.includes("SILVER")),
        isSilver: metal.includes("SILVER") || isSilverMode,
      };
    },
    [productGroupOptionsCache, isSilverMode],
  );

  const calculateFineWt = (nwt, touch) => {
    const nwtVal = parseNumericValue(nwt);
    const touchVal = parseNumericValue(touch);
    if (nwtVal > 0 && touchVal > 0) {
      return (nwtVal * (touchVal / 100)).toFixed(3);
    }
    return "0.000";
  };

  // Resolve masterGoldRate to a scalar per-gram value.
  // In Sales.jsx the prop can be an array (one entry per carat tier); for old-gold
  // URD we always use the 24-carat (pure gold) rate which is index [0].
  const resolvedGoldRateScalar = Array.isArray(masterGoldRate)
    ? Number(masterGoldRate[0] || 0)
    : Number(masterGoldRate) || 0;

  const calculateTotalAmount = useCallback(
    (row) => {
      const fineWt = parseNumericValue(row.fine_wt);
      const ct = parseNumericValue(row.ct);
      const touch = parseNumericValue(row.touch);

      if (!fineWt || fineWt === 0) {
        return "0.00";
      }

      const { isGold, isSilver } = getRowMetalFlags(row);
      if (!isGold && !isSilver) {
        return "0.00";
      }

      let total = 0;
      if (touch && touch > 0) {
        // resolvedGoldRateScalar is already per-gram when masterGoldRate is an array.
        // When it is a scalar, showLiveRate indicates whether it is per-10g (live) or per-gram (daily).
        const goldRatePerGram = Array.isArray(masterGoldRate)
          ? resolvedGoldRateScalar
          : showLiveRate ? resolvedGoldRateScalar / 10 : resolvedGoldRateScalar;
        const silverRatePerGram = showLiveRate ? masterSilverRate / 1000 : masterSilverRate;
        const liveRatePerGram = isGold ? goldRatePerGram : isSilver ? silverRatePerGram : 0;
        total = (touch / 100) * fineWt * liveRatePerGram;
      } else if (ct && ct > 0) {
        if (isGold) {
          const goldRatePerGram = Array.isArray(masterGoldRate)
            ? resolvedGoldRateScalar
            : showLiveRate ? resolvedGoldRateScalar / 10 : resolvedGoldRateScalar;
          const purityRate = (ct / 24) * goldRatePerGram;
          total = purityRate * fineWt;
        } else if (isSilver) {
          const silverRatePerGram = showLiveRate ? masterSilverRate / 1000 : masterSilverRate;
          total = (silverRatePerGram * ct * fineWt) / 999;
        }
      }

      return total > 0 ? total.toFixed(2) : "0.00";
    },
    [getRowMetalFlags, masterGoldRate, masterSilverRate, showLiveRate, resolvedGoldRateScalar],
  );

  // Recalculate totals when live rates change for non-manual rows
  useEffect(() => {
    setTableRows((prev) =>
      prev.map((row) => {
        if (row.isTotalAmtManual) return row;
        const nextTotal = calculateTotalAmount(row);
        if (String(row.total_amt ?? "") === String(nextTotal ?? "")) {
          return row;
        }
        return { ...row, total_amt: nextTotal };
      }),
    );
  }, [masterGoldRate, masterSilverRate, showLiveRate, calculateTotalAmount, setTableRows]);

  const handleTableRowChange = (id, field, value) => {
    setTableRows((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };

          if (field === "gwt") {
            const gwtVal = parseNumericValue(value);
            updatedRow.nwt = gwtVal > 0 ? gwtVal.toFixed(3) : "";
            const lwtVal = parseNumericValue(updatedRow.lwt);
            if (gwtVal > 0 && lwtVal > 0) {
              const diff = gwtVal - lwtVal;
              updatedRow.nwt = diff > 0 ? diff.toFixed(3) : "0.000";
            }
          } else if (field === "lwt") {
            const gwtVal = parseNumericValue(updatedRow.gwt);
            const lwtVal = parseNumericValue(value);
            if (gwtVal > 0) {
              const nwtVal = gwtVal - lwtVal;
              updatedRow.nwt = nwtVal > 0 ? nwtVal.toFixed(3) : "0.000";
            }
          }

          if (
            field === "nwt" ||
            field === "touch" ||
            field === "gwt" ||
            field === "lwt"
          ) {
            updatedRow.fine_wt = calculateFineWt(updatedRow.nwt, updatedRow.touch);
          }

          if (field === "total_amt") {
            updatedRow.isTotalAmtManual = true;
          } else if (
            field === "fine_wt" ||
            field === "touch" ||
            field === "ct" ||
            field === "product_group_id" ||
            field === "nwt" ||
            field === "gwt" ||
            field === "lwt"
          ) {
            updatedRow.isTotalAmtManual = false;
            updatedRow.total_amt = calculateTotalAmount(updatedRow);
          }

          return updatedRow;
        }
        return row;
      }),
    );
  };

  const handleAddTableRow = () => {
    const newId =
      tableRows.length > 0 ? Math.max(...tableRows.map((r) => r.id)) + 1 : 1;
    setTableRows((prev) => [...prev, createDefaultOldPurchaseRow(newId)]);
  };

  const handleRemoveTableRow = (id) => {
    if (tableRows.length > 1) {
      setTableRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  const handleTotalAmtFocus = (rowId) => {
    const isLastRow = rowId === tableRows[tableRows.length - 1].id;
    if (isLastRow) {
      handleAddTableRow();
    }
  };

  const fetchProductGroupByPrefix = useCallback(
    async (rowId, prefix) => {
      const trimmed = String(prefix || "").trim();
      if (trimmed.length < 3) {
        setProductGroupRowResults((prev) => ({ ...prev, [rowId]: [] }));
        setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: false }));
        return;
      }

      if (lastSearchedProductGroupPrefixRef.current[rowId] === trimmed) {
        setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: true }));
        return;
      }

      setProductGroupSearchLoading((prev) => ({ ...prev, [rowId]: true }));
      try {
        const opts = await searchProductGroupSelectOptions(trimmed);
        lastSearchedProductGroupPrefixRef.current[rowId] = trimmed;
        setProductGroupRowResults((prev) => ({ ...prev, [rowId]: opts }));
        setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: true }));
        if (opts.length > 0 && onPoolHeaderChange) {
          onPoolHeaderChange(opts[0]);
        }
      } catch (e) {
        console.error("Error searching product groups:", e);
        setProductGroupRowResults((prev) => ({ ...prev, [rowId]: [] }));
        setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: true }));
      } finally {
        setProductGroupSearchLoading((prev) => ({ ...prev, [rowId]: false }));
      }
    },
    [onPoolHeaderChange],
  );

  const handleProductGroupChange = (rowId, value) => {
    setProductGroupSearchText((prev) => ({ ...prev, [rowId]: value }));
    setProductGroupHighlightedIndex((prev) => ({ ...prev, [rowId]: 0 }));

    if (productGroupSearchTimerRef.current[rowId]) {
      clearTimeout(productGroupSearchTimerRef.current[rowId]);
    }

    if (!value.trim()) {
      handleTableRowChange(rowId, "product_group_id", "");
      handleTableRowChange(rowId, "product_group_name", "");
      setProductGroupDropdownActive((prev) => ({ ...prev, [rowId]: false }));
      setProductGroupRowResults((prev) => ({ ...prev, [rowId]: [] }));
      setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: false }));
      lastSearchedProductGroupPrefixRef.current[rowId] = "";
      if (onPoolHeaderChange) onPoolHeaderChange(null);
      return;
    }

    const hasMinChars = value.trim().length >= 3;
    setProductGroupDropdownActive((prev) => ({
      ...prev,
      [rowId]: hasMinChars,
    }));

    if (hasMinChars) {
      setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: false }));
      productGroupSearchTimerRef.current[rowId] = setTimeout(() => {
        fetchProductGroupByPrefix(rowId, value.trim());
      }, 300);
    } else {
      setProductGroupRowResults((prev) => ({ ...prev, [rowId]: [] }));
      setProductGroupApiCalled((prev) => ({ ...prev, [rowId]: false }));
      lastSearchedProductGroupPrefixRef.current[rowId] = "";
    }
  };

  const handleProductGroupSelect = (rowId, selectedGroup) => {
    const label = productGroupOptionDisplayName(selectedGroup) || selectedGroup.name || selectedGroup.label || "";
    setTableRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              product_group_id: String(selectedGroup.value ?? selectedGroup.id),
              product_group_name: label,
            }
          : r,
      ),
    );
    setProductGroupSearchText((prev) => ({
      ...prev,
      [rowId]: label,
    }));
    if (setProductGroupOptionsCache) {
      setProductGroupOptionsCache((prev) => mergeProductGroupOptions(prev, [selectedGroup]));
    }
    if (onPoolHeaderChange) {
      onPoolHeaderChange(selectedGroup);
    }
    setProductGroupDropdownActive((prev) => ({ ...prev, [rowId]: false }));

    const nextInput = productGroupInputRefs.current[rowId]
      ?.closest("tr")
      ?.querySelector('input[placeholder="0"]');
    if (nextInput) nextInput.focus();
  };

  const handleProductGroupKeyDown = (e, rowId) => {
    const filtered = productGroupRowResults[rowId] || [];

    if (
      !productGroupDropdownActive[rowId] ||
      filtered.length === 0 ||
      productGroupSearchLoading[rowId]
    ) {
      if (e.key === "Enter" || e.key === "Tab") {
        const nextInput = e.target.closest("tr")?.querySelector('input[placeholder="0"]');
        if (nextInput) nextInput.focus();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setProductGroupHighlightedIndex((prev) => {
          const next = Math.min((prev[rowId] || 0) + 1, filtered.length - 1);
          productGroupItemsRefs.current[rowId]?.[next]?.scrollIntoView({ block: "nearest" });
          if (onPoolHeaderChange && filtered[next]) onPoolHeaderChange(filtered[next]);
          return { ...prev, [rowId]: next };
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setProductGroupHighlightedIndex((prev) => {
          const next = Math.max((prev[rowId] || 0) - 1, 0);
          productGroupItemsRefs.current[rowId]?.[next]?.scrollIntoView({ block: "nearest" });
          if (onPoolHeaderChange && filtered[next]) onPoolHeaderChange(filtered[next]);
          return { ...prev, [rowId]: next };
        });
        break;
      case "Enter":
        e.preventDefault();
        handleProductGroupSelect(
          rowId,
          filtered[productGroupHighlightedIndex[rowId] || 0],
        );
        break;
      case "Escape":
      case "Tab":
        setProductGroupDropdownActive((prev) => ({ ...prev, [rowId]: false }));
        break;
      default:
        break;
    }
  };

  const renderProductGroupDropdown = (rowId) => {
    if (!productGroupDropdownActive[rowId]) return null;

    const searchText = productGroupSearchText[rowId] || "";
    if (!searchText.trim() || searchText.trim().length < 3) return null;

    const filtered = productGroupRowResults[rowId] || [];
    const loading = productGroupSearchLoading[rowId];
    const apiCalled = productGroupApiCalled[rowId];
    const inputElement = productGroupInputRefs.current[rowId];
    if (!inputElement) return null;

    const inputRect = inputElement.getBoundingClientRect();
    if (!inputRect) return null;

    const portal = document.getElementById("dropdown-portal") || document.body;
    const maxVisibleItems = 5;
    const itemHeight = 40;
    const scrollableHeight = maxVisibleItems * itemHeight;
    const dropdownWidth = Math.max(inputElement.offsetWidth, 220);

    return ReactDOM.createPortal(
      <div
        ref={(el) => (productGroupDropdownRefs.current[rowId] = el)}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${dropdownWidth}px`,
          minWidth: `${dropdownWidth}px`,
          maxWidth: `${Math.max(inputElement.offsetWidth, 320)}px`,
          maxHeight: `${scrollableHeight + 8}px`,
          height: `${scrollableHeight + 8}px`,
          overflow: "hidden",
          zIndex: portalZIndex,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: 0,
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${scrollableHeight}px`,
            height: `${scrollableHeight}px`,
            overflowY: !loading && filtered.length > maxVisibleItems ? "auto" : "hidden",
            overflowX: "hidden",
            backgroundColor: "#ffffff",
          }}
        >
          {loading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.5rem 1rem", fontSize: "0.85rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((group, idx) => {
              const isHighlighted = idx === (productGroupHighlightedIndex[rowId] || 0);
              const currentRow = tableRows.find((r) => r.id === rowId);
              const isSelected =
                currentRow?.product_group_id &&
                String(group.value ?? group.id) === String(currentRow.product_group_id);
              const label =
                productGroupOptionDisplayName(group) || group.name || group.label || "";
              return (
                <div
                  key={group.value ?? idx}
                  ref={(el) => {
                    if (!productGroupItemsRefs.current[rowId]) {
                      productGroupItemsRefs.current[rowId] = [];
                    }
                    productGroupItemsRefs.current[rowId][idx] = el;
                  }}
                  className={`dropdown-item ${isHighlighted ? "active" : ""}`}
                  style={{
                    cursor: "pointer",
                    padding: "0.45rem 0.75rem",
                    fontSize: "0.85rem",
                    minHeight: `${itemHeight}px`,
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: isHighlighted
                      ? "var(--theme-primary, #1a1a1a)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                    color: isHighlighted ? "#fff" : "inherit",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                  onMouseEnter={() => {
                    setProductGroupHighlightedIndex((prev) => ({
                      ...prev,
                      [rowId]: idx,
                    }));
                    if (onPoolHeaderChange) {
                      onPoolHeaderChange(group);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleProductGroupSelect(rowId, group);
                  }}
                >
                  {label}
                </div>
              );
            })
          ) : apiCalled ? (
            <div
              className="dropdown-item text-muted text-center"
              style={{ cursor: "default", padding: "0.5rem 1rem", fontSize: "0.85rem" }}
            >
              No matching items
            </div>
          ) : null}
        </div>
      </div>,
      portal,
    );
  };

  const totalAmount = tableRows.reduce(
    (sum, row) => sum + parseNumericValue(row.total_amt),
    0,
  );
  const totalPcs = tableRows.reduce(
    (sum, row) => sum + (parseInt(row.pcs, 10) || 0),
    0,
  );
  const totalGwt = tableRows.reduce(
    (sum, row) => sum + parseNumericValue(row.gwt),
    0,
  );
  const totalLwt = tableRows.reduce(
    (sum, row) => sum + parseNumericValue(row.lwt),
    0,
  );
  const totalNwt = tableRows.reduce(
    (sum, row) => sum + parseNumericValue(row.nwt),
    0,
  );

  return (
    <>
      <div
        className={`table-responsive border ${
          buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
        } ${cardClass}`}
      >
        <table className={`table table-bordered mb-0 align-middle ${tableClass}`}>
          <thead style={{ backgroundColor: "#f8f9fa" }}>
            <tr>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "40px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                No.
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ minWidth: "180px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Product Group 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "70px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Pcs
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "80px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Ct 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "95px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                G.Wt 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "95px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                L.Wt
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "95px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                N.Wt 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "85px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Touch 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "95px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Fine Wt 
              </th>
              <th
                className="px-2 py-2 text-center"
                style={{ width: "115px", fontSize: "0.85rem", fontWeight: "600" }}
              >
                Total Amt 
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, rowIndex) => (
              <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                {/* Serial No / Delete on hover */}
                <td className="p-0 text-center" style={{ width: "40px" }}>
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "36px" }}
                  >
                    <span
                      style={{
                        fontSize: "0.85rem",
                        cursor: tableRows.length > 1 ? "pointer" : "default",
                        transition: "opacity 0.2s",
                      }}
                      onClick={() => handleRemoveTableRow(row.id)}
                      onMouseEnter={(e) => {
                        if (tableRows.length > 1) {
                          e.target.textContent = "×";
                          e.target.style.fontWeight = "700";
                          e.target.style.color = "#dc3545";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.textContent = rowIndex + 1;
                        e.target.style.fontWeight = "normal";
                        e.target.style.color = "inherit";
                      }}
                      title={tableRows.length > 1 ? "Delete row" : ""}
                    >
                      {rowIndex + 1}
                    </span>
                  </div>
                </td>

                {/* Product Group */}
                <td
                  className="p-0 position-relative"
                  style={lineCellErrorStyle(
                    fieldErrors[row.id]?.includes("product_group"),
                  )}
                >
                  <input
                    ref={(el) => (productGroupInputRefs.current[row.id] = el)}
                    type="text"
                    className="form-control border-0 rounded-0 shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="Search product group..."
                    value={
                      productGroupSearchText[row.id] !== undefined
                        ? productGroupSearchText[row.id]
                        : row.product_group_name || ""
                    }
                    onChange={(e) => handleProductGroupChange(row.id, e.target.value)}
                    onKeyDown={(e) => handleProductGroupKeyDown(e, row.id)}
                    onFocus={() => {
                      const val = productGroupSearchText[row.id] || "";
                      if (val.trim().length >= 3) {
                        setProductGroupDropdownActive((prev) => ({ ...prev, [row.id]: true }));
                        if (
                          !productGroupRowResults[row.id] ||
                          productGroupRowResults[row.id].length <= 1
                        ) {
                          fetchProductGroupByPrefix(row.id, val.trim());
                        }
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setProductGroupDropdownActive((prev) => ({ ...prev, [row.id]: false }));
                      }, 250);
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                  {renderProductGroupDropdown(row.id)}
                </td>

                {/* Pcs */}
                <td
                  className="p-0"
                  style={lineCellErrorStyle(fieldErrors[row.id]?.includes("pcs"))}
                >
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0"
                    value={row.pcs}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*$/.test(v)) {
                        handleTableRowChange(row.id, "pcs", v);
                      }
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>

                {/* Ct */}
                <td className="p-0">
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.00"
                    value={row.ct}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) {
                        handleTableRowChange(row.id, "ct", v);
                      }
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>

                {/* G.Wt */}
                <td
                  className="p-0"
                  style={lineCellErrorStyle(fieldErrors[row.id]?.includes("gwt"))}
                >
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.000"
                    value={row.gwt}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) {
                        handleTableRowChange(row.id, "gwt", v);
                      }
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>

                {/* L.Wt */}
                <td className="p-0">
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.000"
                    value={row.lwt}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) {
                        handleTableRowChange(row.id, "lwt", v);
                      }
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>

                {/* N.Wt */}
                <td className="p-0">
                  <input
                    type="text"
                    readOnly
                    className="form-control border-0 rounded-0 text-end bg-light shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.000"
                    value={row.nwt}
                    tabIndex={-1}
                  />
                </td>

                {/* Touch */}
                <td className="p-0">
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.00"
                    value={row.touch}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) {
                        handleTableRowChange(row.id, "touch", v);
                      }
                    }}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>

                {/* Fine Wt */}
                <td className="p-0">
                  <input
                    type="text"
                    readOnly
                    className="form-control border-0 rounded-0 text-end bg-light shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.000"
                    value={row.fine_wt}
                    tabIndex={-1}
                  />
                </td>

                {/* Total Amt */}
                <td className="p-0">
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 text-end shadow-none px-2"
                    style={{ fontSize: "0.85rem", height: "36px" }}
                    placeholder="0.00"
                    value={row.total_amt}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d*$/.test(v)) {
                        handleTableRowChange(row.id, "total_amt", v);
                      }
                    }}
                    onFocus={() => handleTotalAmtFocus(row.id)}
                    autoComplete="off"
                    disabled={readOnly}
                  />
                </td>
              </tr>
            ))}

            {/* Totals Row */}
            <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "600" }}>
              <td className="p-0 text-center" style={{ height: "36px" }}>
                <span style={{ fontSize: "0.85rem" }}>Total</span>
              </td>
              <td className="p-0"></td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>{totalPcs}</span>
              </td>
              <td className="p-0"></td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>{totalGwt.toFixed(3)}</span>
              </td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>{totalLwt.toFixed(3)}</span>
              </td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>{totalNwt.toFixed(3)}</span>
              </td>
              <td className="p-0"></td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>
                  {tableRows
                    .reduce((sum, r) => sum + parseNumericValue(r.fine_wt), 0)
                    .toFixed(3)}
                </span>
              </td>
              <td className="p-0 text-end px-2" style={{ backgroundColor: "#fff" }}>
                <span style={{ fontSize: "0.85rem" }}>{totalAmount.toFixed(2)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>


    </>
  );
};

export default OldPurchaseTable;
